/**
 * Capacités réelles de NOAM — carte des Réglages (accordéon factuel).
 * Doctrine : chaque affirmation est adossée à un mécanisme réel du produit.
 * Ton factuel, zéro superlatif marketing. Mise à jour : 2026-09.
 */
import { useState } from "react";
import { BrainCircuit, ChevronDown, ShieldCheck, Workflow, Scale, Network } from "lucide-react";

const SECTIONS = [
  {
    key: "capacites",
    icon: Network,
    title: "Ce que NOAM sait faire aujourd'hui",
    body: [
      ["Ontologie totale", "Un seul monde canonique : organisations, personnes, deals, processus, contrôles, KPI, risques, plans, normes, preuves. Points Nord, Annuaire et Normes sont des perspectives du même graphe — jamais des copies. Navigation hiérarchique (part_of) et transversale (atténue, remédie, mesure, contribue, couvre) avec retour par fil d'ariane."],
      ["Deals", "Score de viabilité 0-100 recalculé à chaque nouvelle information (6 critères pondérés, leviers actionnables, grade AAA→D), re-scoring automatique à l'ajout d'un document, portefeuille scoré, checklist de pièces."],
      ["Marché", "Noam Radar : veille hebdomadaire automatique sur les entités stratégiques (recherche web ciblée, filtrage des signaux déjà vus, criticité). Recherche web en direct dans le chat, avec sources citées."],
      ["Gouvernance", "Référentiels de normes ingérés (ex. nLPD) découpés en exigences, couverture par contrôles/processus, tendances KPI (amélioré / stagné / péjoré) calculées sur observations réelles, preuves multi-objets."],
      ["Publication", "Studio de Présentation : projection HTML autoporteuse validée par l'humain, lien public révocable, journal d'accès, alerte d'obsolescence, thèmes (NOAM sombre, Institutionnel clair)."],
    ],
  },
  {
    key: "metacognition",
    icon: BrainCircuit,
    title: "Métacognition — NOAM connaît les limites de sa connaissance",
    body: [
      ["Statut épistémique partout", "Chaque information est marquée vérifié / déclaré / inféré / inconnu — dans le chat, le scoring, le Studio et les relations du graphe. Une hypothèse ne devient jamais silencieusement un fait."],
      ["Aveu d'ignorance", "Quand la vérification web échoue, le scoring le dit (« déclaré, non vérifié », « recherche indisponible ») au lieu de combler le vide."],
      ["Conscience de sa péremption", "Les présentations partagées signalent elles-mêmes quand elles ne reflètent plus la vérité canonique (alerte d'obsolescence)."],
      ["Provenance tracée", "Chaque relation du graphe porte sa source, son auteur, son niveau de confiance et sa validité temporelle."],
      ["Aucune connaissance par accident", "Une visualisation ne crée jamais de connaissance : les rendus n'écrivent pas dans le graphe ; la résolution d'identité est fail-closed entre tenants."],
      ["Auto-évaluation", "Tribunal de parité (NOAM jugé contre des références) et harnais de régression permanent."],
    ],
  },
  {
    key: "algorithmique",
    icon: Workflow,
    title: "Gestion algorithmique — aide au jugement, jamais d'exécution autonome",
    body: [
      ["Algorithmes déterministes", "Barème de scoring transparent, tendances KPI, obsolescence, anti-cycle du graphe : mêmes entrées → mêmes sorties, auditable."],
      ["Algorithmique IA encadrée", "Notes par critère avec vérification web quand possible, criticité des signaux, lectures IA — toujours qualifiées épistémiquement."],
      ["Automatisation planifiée", "Radar hebdomadaire, brief du matin, digests, re-scoring sur événement : production d'information, pas de décision."],
      ["Ligne rouge", "Aucune décision autonome irréversible, aucune exécution de transaction, aucune publication sans validation humaine. L'humain arbitre, l'algorithme instruit le dossier — vous restez hors du régime des décisions entièrement automatisées (nLPD/RGPD)."],
    ],
  },
  {
    key: "palantir",
    icon: Scale,
    title: "NOAM face aux ontologies opérationnelles (type Palantir)",
    body: [
      ["Même famille", "Comme Foundry, NOAM repose sur une ontologie opérationnelle : objets canoniques, relations, instruments branchés sur un monde unique."],
      ["Différence structurelle : la métacognition", "Palantir sait ce qu'il sait. NOAM sait aussi ce qu'il ne sait pas, et le dit : statuts épistémiques natifs, aveu d'ignorance, conscience d'obsolescence — sans équivalent natif chez Palantir."],
      ["Déploiement", "Palantir : des mois, des ingénieurs déployés, budget très élevé. NOAM : self-serve, l'ontologie se construit en travaillant (Feed, documents, conversations)."],
      ["Posture", "Palantir outille l'action opérationnelle à grande échelle ; NOAM instruit le jugement du dirigeant — chaque recommandation expose ses leviers et ses lacunes."],
      ["Cible", "Palantir : États et très grands groupes. NOAM : PME, mid-market et sociétés de conseil suisses, avec ancrage réglementaire local (nLPD)."],
    ],
  },
];

export const NoamCapabilitiesCard = () => {
  const [open, setOpen] = useState(null);
  return (
    <div className="noam-card p-6 space-y-3" data-testid="noam-capabilities-card">
      <div>
        <h2 className="text-sm font-semibold text-zinc-100 flex items-center gap-2">
          <ShieldCheck className="w-4 h-4 text-[#43C6D8]" /> Capacités réelles de NOAM
        </h2>
        <p className="text-[11px] text-zinc-500 mt-1">
          Ce que le système fait vraiment, aujourd'hui — chaque point correspond à un mécanisme en production. Mise à jour : septembre 2026.
        </p>
      </div>
      <div className="divide-y divide-white/5 border border-white/8 rounded-lg overflow-hidden">
        {SECTIONS.map((s) => (
          <div key={s.key}>
            <button type="button" onClick={() => setOpen(open === s.key ? null : s.key)}
              className="w-full px-4 py-3 flex items-center gap-2.5 text-left hover:bg-white/[0.03] transition-colors"
              data-testid={`capabilities-section-${s.key}`}>
              <s.icon className="w-4 h-4 text-[#43C6D8] shrink-0" />
              <span className="text-[13px] text-zinc-200 flex-1">{s.title}</span>
              <ChevronDown className={`w-3.5 h-3.5 text-zinc-600 transition-transform ${open === s.key ? "rotate-180" : ""}`} />
            </button>
            {open === s.key && (
              <div className="px-4 pb-4 pt-1 space-y-2.5 bg-black/20" data-testid={`capabilities-body-${s.key}`}>
                {s.body.map(([label, text]) => (
                  <div key={label}>
                    <div className="text-[12px] font-medium text-[#8FC1EE]">{label}</div>
                    <div className="text-[12px] text-zinc-400 leading-relaxed">{text}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
